import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { 
  FaCloudUploadAlt,
  FaSpinner, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaFilePdf,
  FaFileWord,
  FaFileAlt,
  FaFile,
  FaTimes
} from 'react-icons/fa';
import './ResumeUploadCard.css';
import { uploadResume, checkUploadResumeStatus } from '../../services/profile';
import Loader from '../common/Loader';

const ResumeUploadCard = ({ onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [resumeName, setResumeName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    status: null, // null, 'uploading', 'processing', 'success', 'error'
    jobId: null,
    message: '',
    progress: 0
  });
  
  const dropAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  // Process the selected file
  const processFile = (selectedFile) => {
    if (selectedFile) {
      // Check file type
      const fileExt = selectedFile.name.split('.').pop().toLowerCase();
      if (!['pdf', 'doc', 'docx', 'txt'].includes(fileExt)) {
        setUploadProgress({
          status: 'error',
          message: 'Invalid file type. Please upload PDF, DOC, DOCX, or TXT files only.'
        });
        return;
      }
      
      // Check file size (5MB max)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setUploadProgress({
          status: 'error',
          message: 'File too large. Maximum size is 5MB.'
        });
        return;
      }
      
      setFile(selectedFile);
      // Generate a default resume name from the file name (removing extension)
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, "");
      setResumeName(fileName);
      // Clear any previous error
      if (uploadProgress.status === 'error') {
        setUploadProgress({
          status: null,
          message: '',
          progress: 0
        });
      }
    }
  };

  // Handle resume name change
  const handleNameChange = (e) => {
    setResumeName(e.target.value);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file || !resumeName.trim()) {
      setUploadProgress({
        status: 'error',
        message: 'Please select a file and provide a resume name',
        progress: 0
      });
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress({
        status: 'uploading',
        message: 'Uploading your resume...',
        progress: 10
      });

      // Create form data for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('resume_name', resumeName);

      // Upload the resume
      const response = await uploadResume(formData);
      
      // If we have a job ID, start polling for status
      if (response && response.job_id) {
        setUploadProgress({
          status: 'processing',
          jobId: response.job_id,
          message: 'Processing your resume...',
          progress: 40
        });
        
        // Start polling for job status
        pollJobStatus(response.job_id);
      } else {
        throw new Error('Upload failed: No job ID returned');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setUploadProgress({
        status: 'error',
        message: error.message || 'Failed to upload resume',
        progress: 0
      });
    }
  };

  // Poll for job status
  const pollJobStatus = async (jobId) => {
    try {
      // Poll every 2 seconds
      const statusInterval = setInterval(async () => {
        try {
          const statusResponse = await checkUploadResumeStatus(jobId);
          
          // Update progress based on status
          let currentProgress = 40;
          if (statusResponse.status === 'completed') {
            currentProgress = 100;
          } else if (statusResponse.status === 'processing') {
            currentProgress = 70;
          }
          
          setUploadProgress(prev => ({
            ...prev,
            progress: currentProgress
          }));
          
          if (statusResponse.status === 'completed') {
            clearInterval(statusInterval);
            setIsUploading(false);
            setUploadProgress({
              status: 'success',
              message: 'Resume uploaded and processed successfully!',
              progress: 100
            });
            
            // Reset file and name after successful upload
            setTimeout(() => {
              setFile(null);
              setResumeName('');
            }, 3000);
            
            // Call the callback to refresh the profiles/resumes list
            if (onUploadComplete) {
              onUploadComplete();
            }
          } else if (statusResponse.status === 'failed') {
            clearInterval(statusInterval);
            setIsUploading(false);
            setUploadProgress({
              status: 'error',
              message: statusResponse.error || 'Resume processing failed',
              progress: 0
            });
          }
          // If still processing, continue polling
        } catch (pollError) {
          clearInterval(statusInterval);
          setIsUploading(false);
          setUploadProgress({
            status: 'error',
            message: pollError.message || 'Failed to check processing status',
            progress: 0
          });
        }
      }, 2000);

      // Stop polling after 2 minutes if still processing
      setTimeout(() => {
        clearInterval(statusInterval);
        if (uploadProgress.status === 'processing') {
          setIsUploading(false);
          setUploadProgress({
            status: 'error',
            message: 'Processing timed out. Please try again later.',
            progress: 0
          });
        }
      }, 120000);
    } catch (error) {
      console.error('Status polling error:', error);
      setIsUploading(false);
      setUploadProgress({
        status: 'error',
        message: error.message || 'Failed to check processing status',
        progress: 0
      });
    }
  };

  // Reset the upload state
  const resetUpload = () => {
    setFile(null);
    setResumeName('');
    setIsUploading(false);
    setUploadProgress({
      status: null,
      jobId: null,
      message: '',
      progress: 0
    });
  };

  // Get file icon based on file type
  const getFileIcon = () => {
    if (!file) return null;
    
    const fileType = file.name.split('.').pop().toLowerCase();
    switch (fileType) {
      case 'pdf':
        return <FaFilePdf className="file-type-icon pdf-icon" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="file-type-icon word-icon" />;
      case 'txt':
        return <FaFileAlt className="file-type-icon text-icon" />;
      default:
        return <FaFile className="file-type-icon" />;
    }
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.add('dragover');
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('dragover');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove('dragover');
    }
    
    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  };

  // Handle browse files click
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Render based on upload status
  if (uploadProgress.status === 'success') {
    return (
      <div className="resume-upload-card success">
        <div className="success-animation">
          <FaCheckCircle className="success-icon" />
        </div>
        <h3>Upload Complete!</h3>
        <p>{uploadProgress.message}</p>
        <button className="reset-button" onClick={resetUpload}>
          Upload Another Resume
        </button>
      </div>
    );
  }

  return (
    <div className="resume-upload-card">
      <h3>Upload Your Resume</h3>
      
      {uploadProgress.status === 'error' && (
        <div className="error-message">
          <FaExclamationTriangle />
          <p>{uploadProgress.message}</p>
        </div>
      )}
      
      <div 
        className={`drop-area ${file ? 'has-file' : ''}`}
        ref={dropAreaRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt"
          className="file-input"
        />
        
        {!file ? (
          <div className="upload-placeholder">
            <div className="upload-icon">
              <FaCloudUploadAlt />
            </div>
            <p>Drag & drop your resume here</p>
            <span className="divider">or</span>
            <button 
              type="button"
              className="browse-button"
              onClick={handleBrowseClick}
            >
              Browse Files
            </button>
            <span className="file-types">Supported formats: PDF, DOC, DOCX, TXT</span>
          </div>
        ) : (
          <div className="file-preview">
            <div className="file-info">
              {getFileIcon()}
              <div className="file-details">
                <span className="file-name">{file.name}</span>
                <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>
            <button 
              type="button"
              className="remove-file"
              onClick={() => setFile(null)}
              disabled={isUploading}
            >
              <FaTimes />
            </button>
          </div>
        )}
      </div>
      
      <div className="resume-name-input">
        <label htmlFor="resumeName">Resume Name</label>
        <input
          type="text"
          id="resumeName"
          value={resumeName}
          onChange={handleNameChange}
          placeholder="Enter a name for this resume"
          disabled={isUploading}
        />
      </div>
      
      {(uploadProgress.status === 'uploading' || uploadProgress.status === 'processing') && (
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress.progress}%` }}
            ></div>
          </div>
          <div className="progress-status">
            <FaSpinner className="spinner" />
            <span>{uploadProgress.message}</span>
          </div>
        </div>
      )}
      
      <button
        className={`upload-button ${isUploading ? 'uploading' : ''}`}
        onClick={handleUpload}
        disabled={!file || !resumeName.trim() || isUploading}
      >
        {isUploading ? 'Processing...' : 'Upload Resume'}
      </button>
    </div>
  );
};

ResumeUploadCard.propTypes = {
  onUploadComplete: PropTypes.func.isRequired
};

export default ResumeUploadCard;